import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export interface DosageForm {
  code: string;
  name: string;
  nameEn: string;
  category: string;
  aliases: string[];
  common_units: string[];
  description: string;
}

export const DOSAGE_FORM_CATEGORIES: Record<string, { name: string; nameEn: string; icon: string }> = {
  ORAL_SOLID: { name: "口服固体制剂", nameEn: "Oral Solid", icon: "💊" },
  ORAL_LIQUID: { name: "口服液体制剂", nameEn: "Oral Liquid", icon: "🧪" },
  INJECTION: { name: "注射剂", nameEn: "Injection", icon: "💉" },
  TOPICAL: { name: "外用制剂", nameEn: "Topical", icon: "🧴" },
  INHALATION: { name: "吸入制剂", nameEn: "Inhalation", icon: "🌬️" },
  OTHER: { name: "其他", nameEn: "Other", icon: "📋" },
};

export const DOSAGE_FORMS: DosageForm[] = [
  { code: "tablet", name: "片剂", nameEn: "Tablet", category: "ORAL_SOLID", aliases: ["片", "素片"], common_units: ["mg", "g"], description: "普通口服片剂" },
  { code: "coated_tablet", name: "薄膜衣片", nameEn: "Film-coated Tablet", category: "ORAL_SOLID", aliases: ["包衣片"], common_units: ["mg", "g"], description: "表面包有薄膜衣层的片剂" },
  { code: "sustained_release_tablet", name: "缓释片", nameEn: "Sustained-release Tablet", category: "ORAL_SOLID", aliases: ["SR片"], common_units: ["mg", "g"], description: "缓慢释放药物的片剂" },
  { code: "controlled_release_tablet", name: "控释片", nameEn: "Controlled-release Tablet", category: "ORAL_SOLID", aliases: ["CR片"], common_units: ["mg", "g"], description: "恒速释放药物的片剂" },
  { code: "enteric_coated_tablet", name: "肠溶片", nameEn: "Enteric-coated Tablet", category: "ORAL_SOLID", aliases: ["EC片"], common_units: ["mg", "g"], description: "在肠道溶解的片剂" },
  { code: "dispersible_tablet", name: "分散片", nameEn: "Dispersible Tablet", category: "ORAL_SOLID", aliases: ["水分散片"], common_units: ["mg", "g"], description: "在水中迅速崩解的片剂" },
  { code: "orally_disintegrating_tablet", name: "口崩片", nameEn: "Orally Disintegrating Tablet", category: "ORAL_SOLID", aliases: ["ODT"], common_units: ["mg", "g"], description: "在口腔内迅速崩解的片剂" },
  { code: "capsule", name: "胶囊剂", nameEn: "Capsule", category: "ORAL_SOLID", aliases: ["胶囊"], common_units: ["mg", "g"], description: "药物装于胶囊壳中" },
  { code: "sustained_release_capsule", name: "缓释胶囊", nameEn: "Sustained-release Capsule", category: "ORAL_SOLID", aliases: ["SR胶囊"], common_units: ["mg", "g"], description: "缓慢释放药物的胶囊" },
  { code: "granule", name: "颗粒剂", nameEn: "Granule", category: "ORAL_SOLID", aliases: ["颗粒"], common_units: ["mg", "g"], description: "颗粒状口服制剂" },
  { code: "powder", name: "散剂", nameEn: "Powder", category: "ORAL_SOLID", aliases: ["粉剂"], common_units: ["mg", "g"], description: "粉末状口服制剂" },
  { code: "syrup", name: "糖浆剂", nameEn: "Syrup", category: "ORAL_LIQUID", aliases: ["糖浆"], common_units: ["mg/ml", "ml"], description: "含糖的口服液体制剂" },
  { code: "oral_solution", name: "口服溶液", nameEn: "Oral Solution", category: "ORAL_LIQUID", aliases: ["口服液"], common_units: ["mg/ml", "ml"], description: "口服液体制剂" },
  { code: "suspension", name: "混悬剂", nameEn: "Suspension", category: "ORAL_LIQUID", aliases: ["混悬液"], common_units: ["mg/ml", "ml"], description: "药物微粒分散在液体中" },
  { code: "emulsion", name: "乳剂", nameEn: "Emulsion", category: "ORAL_LIQUID", aliases: ["乳液"], common_units: ["mg/ml", "ml"], description: "乳状液体制剂" },
  { code: "injection", name: "注射剂", nameEn: "Injection", category: "INJECTION", aliases: ["针剂", "注射液"], common_units: ["mg/ml", "ml"], description: "供注射用的制剂" },
  { code: "lyophilized_powder", name: "冻干粉针", nameEn: "Lyophilized Powder", category: "INJECTION", aliases: ["冻干粉"], common_units: ["mg", "g"], description: "冷冻干燥的注射用粉末" },
  { code: "infusion", name: "大输液", nameEn: "Infusion", category: "INJECTION", aliases: ["输液"], common_units: ["mg/ml", "ml"], description: "静脉滴注用大容量注射液" },
  { code: "ointment", name: "软膏剂", nameEn: "Ointment", category: "TOPICAL", aliases: ["软膏"], common_units: ["%", "g"], description: "半固体外用制剂" },
  { code: "cream", name: "乳膏剂", nameEn: "Cream", category: "TOPICAL", aliases: ["乳膏", "霜剂"], common_units: ["%", "g"], description: "乳状半固体制剂" },
  { code: "gel", name: "凝胶剂", nameEn: "Gel", category: "TOPICAL", aliases: ["凝胶"], common_units: ["%", "g"], description: "凝胶状外用制剂" },
  { code: "patch", name: "贴剂", nameEn: "Patch", category: "TOPICAL", aliases: ["贴片", "透皮贴"], common_units: ["mg", "mg/h"], description: "贴于皮肤的制剂" },
  { code: "eye_drops", name: "滴眼剂", nameEn: "Eye Drops", category: "TOPICAL", aliases: ["眼药水"], common_units: ["mg/ml", "ml"], description: "眼部用药" },
  { code: "ear_drops", name: "滴耳剂", nameEn: "Ear Drops", category: "TOPICAL", aliases: ["耳药水"], common_units: ["mg/ml", "ml"], description: "耳部用药" },
  { code: "nasal_spray", name: "鼻喷剂", nameEn: "Nasal Spray", category: "INHALATION", aliases: ["喷雾剂"], common_units: ["mg", "mg/喷"], description: "鼻腔喷雾制剂" },
  { code: "inhaler", name: "吸入剂", nameEn: "Inhaler", category: "INHALATION", aliases: ["气雾剂"], common_units: ["mg", "μg"], description: "吸入给药制剂" },
  { code: "nebulizer", name: "雾化剂", nameEn: "Nebulizer", category: "INHALATION", aliases: ["雾化液"], common_units: ["mg/ml", "ml"], description: "雾化吸入制剂" },
  { code: "suppository", name: "栓剂", nameEn: "Suppository", category: "OTHER", aliases: ["栓"], common_units: ["mg", "g"], description: "直肠或阴道用固体制剂" },
];

interface DosageFormDropdownProps {
  value: DosageForm | null;
  onChange: (form: DosageForm) => void;
  onClear?: () => void;
}

export default function DosageFormDropdown({ value, onChange, onClear }: DosageFormDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedCategory, setExpandedCategory] = useState<string | null>("ORAL_SOLID");
  const [customInput, setCustomInput] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight?: number;
  }>({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
      const viewportMargin = 12;
      const dropdownGap = 8;
      const availableWidth = Math.max(viewportWidth - viewportMargin * 2, 0);
      const width = Math.min(Math.max(rect.width, 320), availableWidth);
      const desiredHeight = Math.min(
        dropdownRef.current?.scrollHeight || 420,
        Math.max(viewportHeight - viewportMargin * 2, 0),
      );
      const spaceBelow = Math.max(
        viewportHeight - viewportMargin - rect.bottom - dropdownGap,
        0,
      );
      const spaceAbove = Math.max(
        rect.top - viewportMargin - dropdownGap,
        0,
      );
      const openAbove = spaceBelow < desiredHeight && spaceAbove > spaceBelow;
      const availableHeight = openAbove ? spaceAbove : spaceBelow;

      setDropdownPosition({
        top: openAbove ? undefined : rect.bottom + dropdownGap,
        bottom: openAbove ? viewportHeight - rect.top + dropdownGap : undefined,
        left: Math.min(
          Math.max(rect.left, viewportMargin),
          Math.max(viewportMargin, viewportWidth - width - viewportMargin),
        ),
        width,
        maxHeight: Math.min(
          availableHeight,
          Math.max(viewportHeight - viewportMargin * 2, 0),
        ),
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
      const frameId = window.requestAnimationFrame(updatePosition);
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      return () => {
        window.cancelAnimationFrame(frameId);
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredForms = useMemo(() => {
    if (!search.trim()) return DOSAGE_FORMS;
    const q = search.toLowerCase();
    return DOSAGE_FORMS.filter(
      f => f.name.includes(search) || f.nameEn.toLowerCase().includes(q) || f.aliases.some(a => a.toLowerCase().includes(q))
    );
  }, [search]);

  const groupedForms = useMemo(() => {
    const groups: Record<string, DosageForm[]> = {};
    for (const form of filteredForms) {
      if (!groups[form.category]) groups[form.category] = [];
      groups[form.category].push(form);
    }
    return groups;
  }, [filteredForms]);

  const handleSelect = (form: DosageForm) => {
    onChange(form);
    setIsOpen(false);
    setSearch("");
  };

  const handleCustomSubmit = () => {
    if (customInput.trim()) {
      const customForm: DosageForm = {
        code: `custom_${Date.now()}`,
        name: customInput.trim(),
        nameEn: customInput.trim(),
        category: "OTHER",
        aliases: [],
        common_units: [],
        description: "自定义剂型"
      };
      onChange(customForm);
      setIsOpen(false);
      setCustomInput("");
    }
  };

  const dropdownContent = isOpen && (
    <div 
      ref={dropdownRef}
      role="dialog"
      aria-label="选择剂型"
      className="fixed z-[9999] flex max-h-[calc(100vh-24px)] flex-col bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden animate-slideDown"
      style={{
        top: dropdownPosition.top,
        bottom: dropdownPosition.bottom,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        maxHeight: dropdownPosition.maxHeight,
      }}
    >
      <div className="p-3 border-b border-gray-100 dark:border-slate-700">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="搜索剂型..."
          className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 dark:bg-slate-700 dark:text-slate-100"
          autoFocus
        />
      </div>

      <div className="min-h-0 max-h-60 flex-1 overflow-y-auto">
        {Object.entries(groupedForms).map(([category, forms]) => (
          <div key={category}>
            <button
              onClick={() => setExpandedCategory(expandedCategory === category ? null : category)}
              className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700/50 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                <span>{DOSAGE_FORM_CATEGORIES[category]?.icon}</span>
                <span>{DOSAGE_FORM_CATEGORIES[category]?.name}</span>
                <span className="text-xs text-gray-400">({DOSAGE_FORM_CATEGORIES[category]?.nameEn})</span>
              </span>
              <svg className={`w-4 h-4 text-gray-400 transition-transform ${expandedCategory === category ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {expandedCategory === category && (
              <div className="p-2 grid grid-cols-1 gap-1">
                {forms.map(form => (
                  <button
                    key={form.code}
                    onClick={() => handleSelect(form)}
                    className={`px-3 py-2 rounded-lg text-left transition-all ${
                      value?.code === form.code
                        ? "bg-blue-600 text-white"
                        : "hover:bg-blue-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{form.name}</span>
                      <span className={`text-xs ${value?.code === form.code ? "text-blue-100" : "text-gray-400"}`}>
                        {form.nameEn}
                      </span>
                    </div>
                    {form.description && (
                      <div className={`text-xs mt-0.5 ${value?.code === form.code ? "text-blue-100" : "text-gray-400"}`}>
                        {form.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="border-t border-gray-200 dark:border-slate-700 p-3 bg-gray-50 dark:bg-slate-900/50">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          或手动输入剂型：
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCustomSubmit()}
            placeholder="例如：缓释微丸"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 
                     rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500
                     dark:bg-slate-700 dark:text-white"
          />
          <button
            type="button"
            onClick={handleCustomSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 
                     rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 
                     focus:ring-blue-500 transition-colors duration-150"
          >
            确认
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 rounded-xl border text-left flex items-center justify-between transition-all ${
          value
            ? "border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700"
            : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-blue-300"
        }`}
      >
        <div className="flex items-center gap-2">
          {value ? (
            <>
              <span className="text-base">{DOSAGE_FORM_CATEGORIES[value.category]?.icon || "📋"}</span>
              <div>
                <span className="font-medium text-slate-800 dark:text-slate-100">{value.name}</span>
                {value.nameEn !== value.name && (
                  <span className="text-sm text-gray-500 dark:text-slate-400 ml-1">({value.nameEn})</span>
                )}
              </div>
            </>
          ) : (
            <span className="text-gray-400 dark:text-slate-500">选择剂型（必选）</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {value && onClear && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClear(); } }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded cursor-pointer inline-flex items-center justify-center"
              aria-label="清除剂型选择"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}
